import { usePresentationContext } from '@mentimeter/question-modules-contexts';
import { StaggerCss, TitleSection } from '@mentimeter/question-modules-shared';
import { useRagnar } from '@mentimeter/ragnar-react';
import { Box } from '@mentimeter/ragnar-ui/box';
import { ImageProgressive } from '@mentimeter/ragnar-ui/image-progressive';
import { isEmptyObject } from '@tiptap/core';
import { useTranslations } from 'next-intl';
import { getNinePointLayoutProperties } from '../../shared/utils/getNinePointLayoutProperties';
import { PlaceholderImage } from './PlaceholderImage';

export function ImagePresentation() {
  const { useQuestion, useConfig } = usePresentationContext();
  const t = useTranslations('presenting');
  const {
    theme: {
      visualizationColors: { titleColor },
    },
  } = useRagnar();

  const {
    question,
    question_styled,
    cropped_image_url,
    title_meta,
    slide_image_type,
    image_alt_description,
    layout,
  } = useQuestion();
  const { enableAnimations } = useConfig();
  const { InteractiveCanvasComponents } = usePresentationContext();

  const { textAlign, alignItems } = getNinePointLayoutProperties(layout);

  // When we are in Editor always show TitleSection. If we are in a presentation we only show if there is a styled/non-styled title. Otherwise hide since we don't want the container taking up empty space.
  const showTitleSection = Boolean(
    InteractiveCanvasComponents?.CanvasTitleArea ||
      !isEmptyObject(question_styled) ||
      question,
  );

  return (
    <Box
      width="100%"
      flex={1}
      data-testid="image-container"
      alignItems="center"
      justifyContent="center"
    >
      <StaggerCss index={1} type="fade" enableAnimations={enableAnimations}>
        <Box
          flex="1 1 auto"
          width="100%"
          alignItems="center"
          justifyContent="center"
          mb={question || title_meta ? 3 : 0}
        >
          <Box height="100%" width="100%" position="absolute" top={0} left={0}>
            {slide_image_type === 'image-frame' &&
              (cropped_image_url ? (
                <ImageProgressive
                  src={cropped_image_url}
                  alt={image_alt_description || question}
                  stretch
                />
              ) : (
                <PlaceholderImage />
              ))}
          </Box>
        </Box>
      </StaggerCss>
      <Box width="100%" alignItems="center" py="space1" pl="space2">
        {showTitleSection ? (
          <TitleSection
            enablePlaceholderAlignment
            alignItems={alignItems}
            textAlign={textAlign}
            title={question}
            styledTitle={question_styled}
            label={title_meta}
            fontSizeOffset={layout?.font_size_offset}
            mb="space0"
            showTextSizeDropdown={false}
            color={titleColor}
            placeholder={t('image_slide_caption_placeholder')}
          />
        ) : null}
      </Box>
    </Box>
  );
}
